// WNBA data transformation utilities

/**
 * Transform WNBA schedule data to match MLB game structure
 * @param {Object} wnbaSchedule - WNBA schedule data with date keys
 * @param {string} targetDate - Target date in MM/DD/YYYY format
 * @returns {Object} Transformed data with games array and metadata
 */
export const transformWNBASchedule = (wnbaSchedule, targetDate) => {
  // Convert MM/DD/YYYY to YYYYMMDD format for lookup
  const dateParts = targetDate.split('/');
  const wnbaDateKey = `${dateParts[2]}${dateParts[0].padStart(2, '0')}${dateParts[1].padStart(2, '0')}`;
  
  const gamesForDate = wnbaSchedule[wnbaDateKey] || [];
  
  // Transform each game to match MLB structure
  const transformedGames = gamesForDate.map(game => {
    // Find home and away teams
    const homeTeam = game.competitors.find(team => team.isHome) || game.competitors[0];
    const awayTeam = game.competitors.find(team => !team.isHome) || game.competitors[1];
    
    // Extract game time from ISO date
    const gameDate = new Date(game.date);
    const gameTime = gameDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    // Map WNBA status to MLB-like status
    let status = 'Scheduled';
    if (game.status.state === 'post') {
      status = 'Final';
    } else if (game.status.state === 'in') {
      status = 'Live';
    }
    
    return {
      gameIDmlb: game.id, // Keep same field name for compatibility
      gameIDwnba: game.id, // Also add WNBA-specific ID
      away: awayTeam.abbrev,
      awayTeam: awayTeam.shortDisplayName,
      awayLogo: awayTeam.logo,
      home: homeTeam.abbrev, 
      homeTeam: homeTeam.shortDisplayName,
      homeLogo: homeTeam.logo,
      gameTime: gameTime,
      gameDate: game.date,
      status: status,
      sport: 'wnba',
      
      // Additional WNBA-specific data
      venue: game.venue,
      broadcasts: game.broadcasts,
      odds: game.odds,
      tickets: game.tickets,
      completed: game.completed,
      
      // Scores (if available)
      awayScore: awayTeam.score || 0,
      homeScore: homeTeam.score || 0,
      
      // Team colors for styling
      awayTeamColor: awayTeam.teamColor,
      awayAltColor: awayTeam.altColor,
      homeTeamColor: homeTeam.teamColor,
      homeAltColor: homeTeam.altColor
    };
  });
  
  return {
    games: transformedGames,
    sport: 'wnba',
    sportDisplayName: 'WNBA',
    date: targetDate,
    gameDateKey: wnbaDateKey,
    totalGames: transformedGames.length,
    timestamp: new Date().toISOString(),
    format: 'wnba'
  };
};

/**
 * Get WNBA team logo URL helper
 * @param {string} teamAbbrev - Team abbreviation
 * @returns {string} Logo URL
 */
export const getWNBATeamLogoUrl = (teamAbbrev, isDarkMode = false) => {
  if (!teamAbbrev) return '';
  // WNBA logos follow ESPN pattern — always use 500-dark for the app's dark UI
  return `https://a.espncdn.com/i/teamlogos/wnba/500-dark/${String(teamAbbrev).toLowerCase()}.png`;
};

/**
 * WNBA team abbreviation mapping for consistency
 */
export const WNBA_TEAM_MAPPING = {
  'ATL': 'Atlanta Dream',
  'CHI': 'Chicago Sky', 
  'CONN': 'Connecticut Sun',
  'DAL': 'Dallas Wings',
  'GS': 'Golden State Valkyries',
  'IND': 'Indiana Fever',
  'LV': 'Las Vegas Aces',
  'LA': 'Los Angeles Sparks',
  'MIN': 'Minnesota Lynx',
  'NY': 'New York Liberty',
  'PHX': 'Phoenix Mercury',
  'SEA': 'Seattle Storm',
  'WSH': 'Washington Mystics'
};